import {FormikErrors} from 'formik';
import {set} from 'lodash-es';
import {recommendFieldsType} from '../CreateRecommend/recommendFields';
import {modifyChanges} from '../RecommendModify/RecommendModify';
import {PLAN_TYPE_SOURCE} from '../CreateRecommend/RecommendSourceDestination/constants';
import {OVRecommendActionEnum} from '../../../schema-types/globalTypes';
import {RECOMMENDATION_SUBTYPE_TO_SERVER} from '../../../constants/SolutionType';
import {UpsertOvrecommendVariables} from '../../../schema-types/UpsertOvrecommend';
import validateUUID from '../../../utils/validateUUID';
import {
  getPotContactId,
  updateRecommendPotsVariables,
  updateRecsVarsProps,
  getIsMinor,
} from './helpers';
import {getPlanFeesServerFromFormik} from '../CreateRecommend/PlanFees';
import {SaltusAny} from '../../../utils/SaltusAny';

function modifyFieldsToRecommendationNote(fields: recommendFieldsType['modify']): string {
  const changes: string[] = [];
  if (fields?.feeChange) {
    changes.push(modifyChanges.feeChange);
  }
  if (fields?.mandateChange) {
    changes.push(modifyChanges?.mandateChange);
  }
  if (fields?.regularContributionChange) {
    changes.push(modifyChanges?.regularContributionChange);
  }
  if (fields?.regularWithdrawalChange) {
    changes.push(modifyChanges?.regularWithdrawalChange);
  }
  if (fields?.changeOfOwnership) {
    changes.push(modifyChanges?.changeOfOwnership);
  }
  changes.push(fields?.note || '');
  const note = changes.join('\n');
  return note;
}

export const modifyValidator = (
  values: recommendFieldsType,
  errors: SaltusAny
): FormikErrors<recommendFieldsType> => {
  if (!values.goal?.id) {
    if (!values.isSppDa) {
      errors.goal = 'Please select a goal';
    }
  }
  const sourceID = values.modify?.source?.id;
  if (!sourceID) {
    set(errors, 'modify.source', 'Select product type');
  }
  if (
    !values.modify?.feeChange &&
    !values.modify?.mandateChange &&
    !values.modify?.regularContributionChange &&
    !values.modify?.regularWithdrawalChange &&
    !values.modify?.changeOfOwnership
  ) {
    set(errors, 'modify.action', 'At least one must be selected');
  }
  if (!values.modify?.note) {
    if (values.modify?.mandateChange) {
      // ignore
    } else {
      set(errors, 'modify.note', 'A note is required');
    }
  }
  if (values.modify?.mandateChange) {
    if (!values.modify.model) {
      set(errors, 'modify.model', 'A mandate is required');
    }
  }
  /**
   *  if it’s a Saltus SIPP fees and mandates will always go against the GIA gross
   */
  if (
    (values.modify?.feeChange || values.modify?.mandateChange) &&
    values.mainPlanType === 'pension' &&
    !values.modify.source.isChild &&
    values.modify.source.isSaltus
  ) {
    set(
      errors,
      'modify.source',
      "If it's a Saltus SIPP, fees and mandates will always go against the GIA gross"
    );
  }
  return errors;
};

export const getModifyVariables = async ({
  accountId,
  goalId,
  ovrecommendId,
  values,
  recommendDetails,
  contacts = [],
}: updateRecsVarsProps): Promise<UpsertOvrecommendVariables> => {
  const {contact, urgency, urgencyReason, modify} = values;
  const isMinor = getIsMinor(contact, contacts);

  return {
    action: OVRecommendActionEnum.MODIFY,
    solutionType: RECOMMENDATION_SUBTYPE_TO_SERVER.PRODUCT_BASED,
    ovrecommendId: validateUUID(ovrecommendId),
    accountId: validateUUID(accountId),
    goalId: validateUUID(goalId),
    contactId: getPotContactId(contact, isMinor),
    urgency,
    urgencyReason,
    note: modifyFieldsToRecommendationNote(modify),
  };
};

export const generateModifyOVRPots = async ({
  accountId,
  pots,
  ovrecommendId,
  generateRecommendationPots,
  values,
}: updateRecommendPotsVariables) => {
  let potId = values.modify?.source.id;
  const pot = pots ? pots.find((x) => x.id === potId) : null;
  if (pot?.parent?.id) {
    potId = pot.parent.id;
  }
  if (potId) {
    await generateRecommendationPots({
      variables: {
        accountId: validateUUID(accountId)!,
        ovrecommendId: validateUUID(ovrecommendId)!,
        args: [
          {
            potId: validateUUID(potId),
            planType: PLAN_TYPE_SOURCE,
            mandate:
              typeof values.modify?.model === 'string' ? values.modify.model : undefined,
            modelPortfolioId:
              typeof values.modify?.model !== 'string'
                ? validateUUID(values.modify?.model?.id)
                : undefined,
            fees: values.modify?.planFees
              ? getPlanFeesServerFromFormik(values.modify?.planFees)
              : undefined,
          },
        ],
      },
    });
  }
};
